import { useState } from 'react';
import { PhotoIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { ItemHeader } from './SortableList';
import { HazardArea, SpotPhoto } from './spotPhoto';
import { Card, INPUT, LABEL } from './adminUi';

// One Spot the Risk scene: a site photo, what to look for, and the hazards.
// Tap the photo to add a hazard, pick a size, and say what it is — that line
// is what people read afterwards for the ones they missed.

// Photos are kept with the Quest (see the tech server's addQuestSpotTheRisk.js),
// so they are shrunk here first: a phone photo is several MB, this is ~200 KB.
const MAX_SIDE = 1600;
export function resizePhoto(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, MAX_SIDE / Math.max(img.naturalWidth, img.naturalHeight));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.naturalWidth * scale);
      canvas.height = Math.round(img.naturalHeight * scale);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL('image/jpeg', 0.82));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('That file is not a photo this browser can open.')); };
    img.src = url;
  });
}

// Radius as a fraction of the photo's width. ry keeps the on-screen circle
// round on a photo of any shape: it is set from the photo's aspect when the
// hazard is added and scaled with rx after that.
const SIZES = [['Small', 0.05], ['Medium', 0.08], ['Large', 0.13]];

export const blankScene = () => ({ prompt: '', image_url: '', hazards: [], explanation: '' });

function SceneEditor({ q, index, locked, onChange, onRemove, handle, open, onToggle }) {
  const [selected, setSelected] = useState(null);
  const [photoError, setPhotoError] = useState('');
  const set = (patch) => onChange({ ...q, ...patch });
  const setHazard = (i, patch) => set({ hazards: q.hazards.map((h, j) => (j === i ? { ...h, ...patch } : h)) });

  const pickPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      setPhotoError('');
      // a different photo makes the old hazard positions meaningless
      set({ image_url: await resizePhoto(file), hazards: [] });
      setSelected(null);
    } catch (err) { setPhotoError(err.message); }
  };

  const addHazard = ({ x, y, aspect }) => {
    if (locked || q.hazards.length >= 12) return;
    const rx = SIZES[1][1];
    set({ hazards: [...q.hazards, { x, y, rx, ry: rx * aspect, label: '' }] });
    setSelected(q.hazards.length);
  };

  const resize = (i, rx) => {
    const h = q.hazards[i];
    setHazard(i, { rx, ry: h.ry * (rx / h.rx) });
  };

  const removeHazard = (i) => {
    set({ hazards: q.hazards.filter((_, j) => j !== i) });
    setSelected(null);
  };

  return (
    <Card className="px-3 py-2">
      <ItemHeader handle={handle} locked={locked} open={open} onToggle={onToggle} onRemove={onRemove}
        number={index + 1} thumb={q.image_url || null}
        title={q.prompt || (q.image_url ? `${q.hazards.length} hazard${q.hazards.length === 1 ? '' : 's'} marked` : '')}
        problem={!q.image_url ? 'no photo yet' : !q.hazards.length ? 'no hazards marked' : q.hazards.some((h) => !h.label?.trim()) ? 'a hazard has no description' : null} />
      {open ? (
      <div className="px-1 pb-2 pt-3">
      <label className="block">
        <span className={LABEL}>What to look for (optional)</span>
        <input value={q.prompt} onChange={(e) => set({ prompt: e.target.value })} disabled={locked}
          placeholder={`Spot the ${q.hazards.length || 'five'} hazard${q.hazards.length === 1 ? '' : 's'}`} className={INPUT} />
      </label>

      <div className="mt-3">
        {q.image_url ? (
          <>
            <p className="mb-1 text-xs text-gray-500">
              {locked ? 'The hazards on this photo.'
                : 'Tap the photo to mark a hazard. Tap a circle to select it.'}
            </p>
            <SpotPhoto src={q.image_url} onTap={locked ? null : addHazard}>
              {q.hazards.map((h, i) => (
                <HazardArea key={i} h={h} n={i + 1} tone={selected === i ? 'found' : 'brand'}
                  onClick={locked ? null : () => setSelected(i)} />
              ))}
            </SpotPhoto>
          </>
        ) : null}
        {!locked ? (
          <label className="mt-2 inline-flex cursor-pointer items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50">
            <PhotoIcon className="h-4 w-4" />
            {q.image_url ? 'Change photo' : 'Choose a photo'}
            <input type="file" accept="image/*" onChange={pickPhoto} className="hidden" />
          </label>
        ) : null}
        {photoError ? <p className="mt-1 text-sm text-rose-600">{photoError}</p> : null}
      </div>

      {q.hazards.length ? (
        <>
          <p className={`${LABEL} mt-3`}>Hazards ({q.hazards.length})</p>
          <ul className="space-y-2">
            {q.hazards.map((h, i) => (
              <li key={i} onFocus={() => setSelected(i)}
                className={`flex flex-wrap items-center gap-2 rounded-md p-1 ${selected === i ? 'bg-emerald-50' : ''}`}>
                <span className="w-6 text-center text-sm font-bold text-gray-500">{i + 1}</span>
                <input value={h.label} onChange={(e) => setHazard(i, { label: e.target.value })} disabled={locked}
                  placeholder="What is the hazard? e.g. Ladder not footed" className={`${INPUT} min-w-[200px] flex-1`} />
                {!locked ? (
                  <>
                    <span className="flex rounded-md bg-gray-100 p-0.5">
                      {SIZES.map(([label, rx]) => (
                        <button key={label} type="button" onClick={() => resize(i, rx)}
                          className={`rounded px-2 py-1 text-xs font-semibold ${
                            Math.abs(h.rx - rx) < 0.001 ? 'bg-white text-brand-dark shadow' : 'text-gray-600'}`}>
                          {label}
                        </button>
                      ))}
                    </span>
                    <button type="button" aria-label="Remove hazard" onClick={() => removeHazard(i)}
                      className="rounded p-1.5 text-gray-400 hover:bg-gray-100"><XMarkIcon className="h-4 w-4" /></button>
                  </>
                ) : null}
              </li>
            ))}
          </ul>
        </>
      ) : null}

      <label className="mt-3 block">
        <span className={LABEL}>Explanation (shown after they finish, optional)</span>
        <input value={q.explanation} onChange={(e) => set({ explanation: e.target.value })} disabled={locked}
          className={INPUT} />
      </label>
      </div>
      ) : null}
    </Card>
  );
}

export default SceneEditor;
